import { computed, ref } from "vue";
import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";

import { getSpeckleUserStatus, type SpeckleUserStatusResponse } from "@/api/services/Speckle";

/**
 * User-level Speckle account status — "does *my* login have a linked Speckle account?".
 * Project-level gating (does the project have a stream?) stays on `project.speckleData`.
 *
 * Consumers: SpeckleModal (force-refreshes on open and after creating an account),
 * SpeckleCallbackPage (refreshes after the OAuth connect) and the Material Mapping
 * CreateGroupMenu (gates the IFC model menu item on `isLinked`).
 */
export const useSpeckleUser = defineStore("speckleUser", () => {
  // ///////////////////
  // State
  // ///////////////////
  const userStatus = ref<SpeckleUserStatusResponse | null>(null);
  const isLoadingStatus = ref(false);
  const hasStatusError = ref(false);
  // Distinguishes "never asked" from "asked and the answer was none/unlinked".
  const hasFetchedStatus = ref(false);

  // In-flight dedupe: concurrent callers (e.g. CreateGroupMenu mount + modal open) share
  // one request — mirrors the getSpeckleToken() cache pattern in api/services/Speckle.ts.
  // This promise never rejects; failures land in hasStatusError + Sentry.
  let statusPromise: Promise<void> | null = null;

  // ///////////////////
  // Computed
  // ///////////////////
  const isLinked = computed(() => userStatus.value?.status === "linked");
  const hasUnlinkedAccount = computed(() => userStatus.value?.status === "unlinked");

  // ///////////////////
  // Actions
  // ///////////////////
  const runStatusRequest = (): Promise<void> => {
    if (!statusPromise) {
      isLoadingStatus.value = true;
      hasStatusError.value = false;
      statusPromise = (async () => {
        try {
          userStatus.value = await getSpeckleUserStatus();
        } catch (statusError) {
          // Keep the previous status on failure — consumers fail closed via hasStatusError.
          hasStatusError.value = true;
          Sentry.captureException(statusError);
        } finally {
          hasFetchedStatus.value = true;
          isLoadingStatus.value = false;
          statusPromise = null;
        }
      })();
    }
    return statusPromise;
  };

  /**
   * Cheap to call from any consumer's setup: fetches only when nothing usable is cached.
   * A previous error is retried so one transient failure doesn't stick for the session.
   */
  const ensureUserStatus = (): Promise<void> => {
    if (hasFetchedStatus.value && !hasStatusError.value) return Promise.resolve();
    return runStatusRequest();
  };

  /**
   * Force refresh — used on modal open and after create/connect mutations. Waits out any
   * in-flight request first so the refreshed value can never come from a response that
   * predates the caller's mutation.
   */
  const refreshUserStatus = async (): Promise<void> => {
    if (statusPromise) await statusPromise;
    return runStatusRequest();
  };

  return {
    // state
    userStatus,
    isLoadingStatus,
    hasStatusError,
    hasFetchedStatus,

    // computed
    isLinked,
    hasUnlinkedAccount,

    // actions
    ensureUserStatus,
    refreshUserStatus,
  };
});

export default useSpeckleUser;
